// src/components/MobileNav.js
import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

const C = {
  gray900: '#0F172A', white: '#FFFFFF', gray400: '#94A3B8', gray200: '#E2E8F0', gray600: '#475569',
  blue: '#2563EB', blueDark: '#1E40AF', blueLight: '#EFF6FF',
};

// Renders a bottom tab bar (the items flagged mobilePriority in navItems)
// plus a "More" tab that opens a full-screen sheet listing every other
// item -- a standard mobile pattern, since a phone screen can't fit 10+
// nav items the way a desktop sidebar can.
export default function MobileNav({ navItems, isDark }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({}); // { [groupLabel]: true } -- collapsed by default
  const location = useLocation();

  const priorityItems = navItems.filter(i => i.show && i.mobilePriority && !i.children);
  const overflowItems = navItems.filter(i => i.show && !i.mobilePriority);
  const isMoreActive = overflowItems.some(i =>
    i.children ? i.children.some(c => c.path === location.pathname) : i.path === location.pathname
  );

  const barBg = isDark ? '#1E3A5F' : C.blueLight;
  const borderColor = isDark ? C.blueDark : C.blue;
  const inactiveColor = isDark ? '#94A3B8' : C.gray600;

  return (
    <>
      <nav style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 40,
        display: 'flex', background: barBg, borderTop: `2px solid ${borderColor}`,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        boxShadow: '0 -2px 12px rgba(15,23,42,0.06)',
      }}>
        {priorityItems.map(item => (
          <NavLink key={item.path} to={item.path}
            style={({ isActive }) => ({
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '9px 4px', textDecoration: 'none', position: 'relative',
            })}>
            {({ isActive }) => (
              <>
                {isActive && (
                  <span style={{ position: 'absolute', top: 0, left: '25%', right: '25%', height: 3, borderRadius: 2, background: item.accent }} />
                )}
                <span style={{ fontSize: 12, fontWeight: isActive ? 800 : 600, color: isActive ? item.accent : inactiveColor }}>{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
        <button onClick={() => setMoreOpen(true)}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '9px 4px', border: 'none', background: 'none', cursor: 'pointer', position: 'relative',
          }}>
          {isMoreActive && (
            <span style={{ position: 'absolute', top: 0, left: '25%', right: '25%', height: 3, borderRadius: 2, background: C.gray900 }} />
          )}
          <span style={{ fontSize: 12, fontWeight: isMoreActive ? 800 : 600, color: isMoreActive ? C.gray900 : inactiveColor }}>More</span>

        </button>
      </nav>

      {moreOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: barBg, overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 16px 12px', borderBottom: `1px solid ${borderColor}` }}>
            <span style={{ fontWeight: 800, fontSize: 17, color: isDark ? C.white : C.gray900 }}>More</span>
            <button onClick={() => setMoreOpen(false)}
              style={{ border: 'none', background: 'none', fontSize: 22, color: inactiveColor, cursor: 'pointer', padding: 4 }}>✕</button>
          </div>
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {overflowItems.map(item => item.children ? (
              <div key={item.label} style={{ marginBottom: 6 }}>
                <button onClick={() => setExpandedGroups(prev => ({ ...prev, [item.label]: !prev[item.label] }))}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    border: 'none', background: 'none', cursor: 'pointer', padding: '6px 10px',
                  }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: inactiveColor, textTransform: 'uppercase' }}>{item.label}</span>
                  <span style={{ fontSize: 12, color: inactiveColor, transform: (expandedGroups[item.label] ?? item.children.some(c => c.path === location.pathname)) ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▶</span>
                </button>
                {(expandedGroups[item.label] ?? item.children.some(c => c.path === location.pathname)) && item.children.filter(c => c.show !== false).map(child => (
                  <NavLink key={child.path} to={child.path} onClick={() => setMoreOpen(false)}
                    style={({ isActive }) => ({
                      display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderRadius: 10,
                      textDecoration: 'none', fontSize: 14.5, fontWeight: 600,
                      background: isActive ? `${item.accent}18` : 'transparent',
                      color: isActive ? item.accent : (isDark ? '#CBD5E1' : C.gray900),
                    })}>
                    {child.label}
                  </NavLink>
                ))}
              </div>
            ) : (
              <NavLink key={item.path} to={item.path} onClick={() => setMoreOpen(false)}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: 12, padding: '13px 14px', borderRadius: 10,
                  textDecoration: 'none', fontSize: 15, fontWeight: 700,
                  background: isActive ? `${item.accent}18` : 'transparent',
                  color: isActive ? item.accent : (isDark ? '#E2E8F0' : C.gray900),
                })}>
                <span style={{
                  width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: `${item.accent}26`, flexShrink: 0,
                }}>
                  <span style={{ width: 17, height: 17, display: 'block', color: item.accent }}>{item.icon}</span>
                </span>
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
